import React, { useContext, useMemo, useState } from "react";
import TextInputLabeled from "../../../components/Atomos/Inputs/TextInputLabeled";
import { InputTypeEnum } from "../../../types/enums/Inputs";
import RemoveX from "../../../components/Atomos/Icons/RemoveX";
import Checkmark from "../../../components/Atomos/Icons/Checkmark";
import AuthContext from "../../../store/AuthContext";
import { axios } from '../../..';
import { getCookie } from "../../../utils/cookies";
import chevronRightButton from "../../../assets/Fonts/chevron-right-button.svg";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";

const passwordValidationRegex = [
  {
    condition: (password: string) => password.length >= 8,
    message: "Minimum 8 characters",
  },
  {
    condition: (password: string) => /[A-Z]/.test(password),
    message: "At least 1 capital letter",
  },
  {
    condition: (password: string) => /[a-z]/.test(password),
    message: "At least 1 lowercase letter",
  },
  {
    condition: (password: string) => /\d/.test(password),
    message: "At least 1 number",
  },
  {
    condition: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
    message: "At least special character",
  },
];

export default function ChangePassword(): React.ReactElement {
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const { loggedPerson } = useContext(AuthContext);

  const isPasswordValid = useMemo(() => {
    return passwordValidationRegex.every((validation) =>
      validation.condition(password)
    );
  }, [password]);

  function catchErrors(): boolean {
    let error = false;
    if (!currentPassword || !password || !confirmPassword) {
      setError("All fields are required");
      error = true;
      return error;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match. Please try again.");
      error = true;
      return error;
    }

    if (!isPasswordValid) {
      setError("New password does not meet requirements");
      error = true;
      return error;
    }
    if (password === currentPassword) {
      setError("New password is the same as current password.");
      error = true;
      return error;
    }
    return error;
  }

  const handleChangePassword = async () => {
    setError("");

    if (!loggedPerson) {
      setError("User information not found");
      return;
    }

    if (catchErrors()) {
      return;
    }

    try {
      const username =
        sessionStorage.getItem("username") || getCookie("username");
      await axios.Put("/password", {
        username: username || loggedPerson.firmEmail,
        currentPassword: currentPassword,
        newPassword: password,
      });
      setSuccess("Password changed successfully");
      setTimeout(() => {
        setSuccess("");
      }, 5000);
      setCurrentPassword("");
      setPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred while changing password";
      setError(errorMessage);
    }
  };

  return (
    <>
      <div className="w-full h-full lg:py-6 lg:pl-12 lg:pr-6 flex flex-col gap-y-4 lg:gap-y-8">
        <div className="flex flex-row gap-x-6 items-center">
          <h4 className="font-decimal text-[#484F56] font-[600] text-left text-sm lg:text-xl">
            Change Password
          </h4>
        </div>
        <section className="flex flex-col lg:flex-row gap-x-3 gap-y-6">
          <div className="flex flex-col gap-y-6 w-full lg:w-4/5">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded text-center">
                {success}
              </div>
            )}
            <TextInputLabeled
              label="Type your current password"
              value={currentPassword}
              onChange={setCurrentPassword}
              type={InputTypeEnum.PASSWORD}
            />

            {/* Password validation list - visible on mobile/tablet below current password */}
            <div className="flex flex-col gap-y-6 w-full lg:hidden">
              <ul className="list-disc list-inside text-left space-y-4">
                {passwordValidationRegex.map((validation) => (
                  <li
                    key={validation.message}
                    className={`flex flex-row gap-x-2 items-center font-semibold roboto-font ${validation.condition(password) ? "text-gray-font" : "text-feedback-error-dark"}`}
                  >
                    <div className="w-4 h-4">
                      {validation.condition(password) ? (
                        <Checkmark className="fill-gray-font" />
                      ) : (
                        <RemoveX className="fill-feedback-error-dark" />
                      )}
                    </div>
                    {validation.message}
                  </li>
                ))}
              </ul>
            </div>

            <TextInputLabeled
              label="Type your new password"
              value={password}
              onChange={setPassword}
              type={InputTypeEnum.PASSWORD}
            />
            <TextInputLabeled
              label="Retype your new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              type={InputTypeEnum.PASSWORD}
            />
            <LegacyButton
              text="Save"
              onClick={handleChangePassword}
              className={`${isPasswordValid ? "!opacity-100" : "!opacity-50"} w-[50%] ml-auto `}
              disabled={!isPasswordValid}
              content="textIcon"
              icon={
                <img
                  src={chevronRightButton}
                  alt="chevron"
                  className="w-3 h-3"
                />
              }
            />
          </div>

          {/* Password validation list - visible on desktop/tablet on the right side */}
          <div className="hidden lg:flex flex-col gap-y-6 w-full items-center justify-center">
            <ul className="list-disc list-inside space-y-4">
              {passwordValidationRegex.map((validation) => (
                <li
                  key={validation.message}
                  className={`flex flex-row gap-x-2 items-center font-semibold roboto-font ${validation.condition(password) ? "text-feedback-success" : "text-feedback-error-dark"}`}
                >
                  <div className="w-4 h-4">
                    {validation.condition(password) ? (
                      <Checkmark className="fill-gray-font" />
                    ) : (
                      <RemoveX className="fill-feedback-error-dark" />
                    )}
                  </div>
                  {validation.message}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </>
  );
}
